import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TraceLogService } from './trace-log.service';
import { TraceLogFlushService } from './trace-log-flush.service';
import { orDefault } from 'src/app/services/value.util';

@Injectable()
export class ErrorsHandler implements ErrorHandler {

  constructor(
    private injector: Injector
  ) { }

  private getToastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  handleError(error: Error | HttpErrorResponse) {
    const traceLogService = this.injector.get(TraceLogService);
    const flushService = this.injector.get(TraceLogFlushService);

    if (error instanceof HttpErrorResponse) {
      if (!navigator.onLine) {
        this.getToastr().error('Please check your Internet Connection');
        traceLogService.logEvent('CLIENT-ERROR', 'client-error', {
          errorMessage: 'Offline - no internet connection'
        });
      } else if (error.status === 401) {
        this.getToastr().error('Your session has expired', 'Error 401');
        traceLogService.logEvent('AUTH-EVENT', 'auth-event', {
          errorMessage: 'Session expired - forced logout (401)'
        });
      } else {
        this.getToastr().error("Something's gone wrong", 'Error');
        traceLogService.logEvent('CLIENT-ERROR', 'client-error', {
          errorMessage: error.message || 'HTTP error',
          stack: (error as any).stack || null
        });
      }
    } else {
      traceLogService.logEvent('CLIENT-ERROR', 'client-error', {
        errorMessage: orDefault(orDefault(error?.message, error?.toString()), 'Unknown client error'),
        stack: orDefault(error?.stack, undefined)
      });
    }

    flushService.flush();
    console.error('It happens: ', error);
  }

  /**
   * Catches errors OUTSIDE Angular's zone (window.onerror) and
   * unhandled promise rejections — call this once from app.component.ts.
   */
  registerGlobalListeners(): void {
    const traceLogService = this.injector.get(TraceLogService);
    const flushService = this.injector.get(TraceLogFlushService);

    window.addEventListener('error', (event: ErrorEvent) => {
      traceLogService.logEvent('WINDOW-ERROR', 'client-error', {
        errorMessage: event.message,
        stack: event.error?.stack || null
      });
      flushService.flush();
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      traceLogService.logEvent('UNHANDLED-REJECTION', 'unhandled-rejection', {
        errorMessage: reason?.message || String(reason),
        stack: reason?.stack || null
      });
      flushService.flush();
    });
  }
}